import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Typography, Link } from "@castlery/fortress";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useChatStore } from "@/stores/chatStore";
import { formatCurrency } from "@/utils";
import {
  BUTTON_TAG,
  CLICK_ACTION,
  TRACK_EVENT,
  trackEvent,
} from "@/utils/analytics";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorContent } from "./Message";

// 订单数据接口
interface OrderData {
  number: string;
  reference_number: string;
  order_status: string;
  total: string;
  completed_at: string;
  email: string;
  bill_address: {
    phone: string;
  };
}

const formatDate = (placed: string) => {
  const date = new Date(placed);
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hour = date.getHours() % 12 || 12; // 转换为12小时制，0点显示为12
  const minute = date.getMinutes().toString().padStart(2, "0");
  const ampm = date.getHours() >= 12 ? "PM" : "AM";
  return `${month} ${day} ${year}, ${hour}:${minute} ${ampm}`;
};

// 单个订单卡片组件
const OrderCardItem: React.FC<{ item: OrderData }> = ({ item }) => {
  const { market } = useChatStore();

  const {
    number,
    reference_number,
    order_status,
    total,
    completed_at,
    email,
    bill_address,
  } = item;

  return (
    <div className="p-4 space-y-3 bg-[#F6F3E7]">
      {/* 第一行：ORDER NO 和 ORDER PLACED */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Typography level="subh3" className="text-[#616161]">
            ORDER NO
          </Typography>
          <Typography level="caption1">{reference_number}</Typography>
        </div>
        <div>
          <Typography level="subh3" className="text-[#616161]">
            ORDER PLACED
          </Typography>
          <Typography level="caption1">{formatDate(completed_at)}</Typography>
        </div>
      </div>

      {/* 第二行：TOTAL 和 ORDER STATUS */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Typography level="subh3" className="text-[#616161]">
            TOTAL
          </Typography>
          <Typography level="caption1">
            {formatCurrency(Number(total), "SGD")}
          </Typography>
        </div>
        <div>
          <Typography level="subh3" className="text-[#616161]">
            ORDER STATUS
          </Typography>
          <Typography level="caption1" style={{ textTransform: "capitalize" }}>
            {order_status ? order_status.toLowerCase().replace(/_/g, " ") : ""}
          </Typography>
        </div>
      </div>

      {/* 第三行：CONTACT 和 View Details */}
      <div>
        <Typography level="subh3" className="text-[#616161]">
          CONTACT
        </Typography>
        <div className="flex justify-between items-end">
          <Typography className="flex-1 mr-4 break-all" level="caption1">
            {email || bill_address?.phone || ""}
          </Typography>
          <Link
            variant="primary"
            size="sm"
            onClick={() => {
              trackEvent({
                event: TRACK_EVENT,
                category: "order_list",
                action: CLICK_ACTION,
                tag: BUTTON_TAG,
                tag_value: "view_details",
              });

              if (market) {
                window.open(
                  `/${market.toLocaleLowerCase()}/account/order-details?number=${number}`,
                  "_self",
                );
              }
            }}
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};

const OrderCardWithoutErrorBoundary = (props: {
  data: { orders: OrderData[] };
}) => {
  const { orders = [] } = props.data;

  const [isExpanded, setIsExpanded] = useState(false);

  const maxVisibleItems = 3;
  const hasMoreItems = orders.length > maxVisibleItems;
  const visibleItems = isExpanded ? orders : orders.slice(0, maxVisibleItems);

  if (orders.length === 0) {
    return null;
  }

  return (
    <Card className="w-full rounded-md rounded-tl-none border border-[#F6F3E7] bg-[#FBF9F4] text-[#3C101E]">
      <CardContent className="p-3 flex flex-col gap-3 font-aime">
        {visibleItems.map((item, index) => (
          <OrderCardItem key={`order-${item.number}-${index}`} item={item} />
        ))}

        {hasMoreItems && (
          <div className="flex items-center justify-center">
            <Link
              variant="secondary"
              size="xs"
              endDecorator={
                isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )
              }
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "View less" : "View more"}
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const OrderCard = (props: { data: { orders: OrderData[] } }) => {
  return (
    <ErrorBoundary fallback={<ErrorContent position="left" />}>
      <OrderCardWithoutErrorBoundary {...props} />
    </ErrorBoundary>
  );
};
